import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Col, Form, FormGroup, Label, Row, Spinner } from "reactstrap";
import DataTable from "../../shared/base-components/DataTable";
import paths from "../../shared/route/paths";
import { useCustomersMaintenancesListMutation } from "./Maintenance.actions";
import useCookie from "../../shared/hooks/useCookie";
import TextField from "../../shared/base-components/TextField";
import { SubmitHandler, useForm } from "react-hook-form";
import Button from "../../shared/base-components/Button";
import { CustomerMaintenancesResponse } from "./Maintenance.constants";
import { useRecoilState } from "recoil";
import {
  filterSelector,
  filterSelectorProps,
} from "../../shared/actions/LayoutMode.actions";

const CustomerList = () => {
  document.title =
    "Müşteriler | Bu ekranda müşteri ekleme güncelleme yapabilirsiniz.";
  const isMobile: boolean = /iPhone|iPad|iPod|Android/i.test(
    navigator.userAgent
  );

  const { data, mutate, isLoading } = useCustomersMaintenancesListMutation();

  const [_, setCookieCustomerProfileId] = useCookie("customerProfileId");
  const [filteredMaintenancesList, setFilteredMaintenancesList] =
    useState<any>(data);
  const [filterState, setFilterState] = useRecoilState(filterSelector);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit: SubmitHandler<any> = (requestValues: any) => {
    setFilterState((prevState: filterSelectorProps) => ({
      ...prevState,
      minDays: requestValues.startDay,
      maxDays: requestValues.endDay,
    }));

    const filteredData = filterByRemainingDays(
      data,
      requestValues.startDay,
      requestValues.endDay
    );

    setFilteredMaintenancesList(filteredData);
  };

  useEffect(() => {
    mutate({});
  }, []);

  useEffect(() => {
    if (filterState.minDays.length || filterState.maxDays.length) {
      const filteredData = filterByRemainingDays(
        data,
        filterState.minDays,
        filterState.maxDays
      );

      setFilteredMaintenancesList(filteredData);
    }
  }, [data]);

  const columns = useMemo(
    () => [
      {
        Header: "Ad Soyad",
        filterable: false,
        disableFilters: true,
        accessor: "fullname",
        Cell: (response: any) => (
          <>
            <h5 className="font-size-14 mb-1">
              <Link
                onClick={() =>
                  setCookieCustomerProfileId(response.row.original?.id, 90)
                }
                to={paths.CUSTOMER.CUSTORMER_PROFILE}
                className="text-dark"
              >
                {response.row.original?.fullname}
              </Link>
            </h5>
          </>
        ),
      },
      {
        Header: "İl",
        accessor: "country",
        Cell: (response: any) => {
          const data = response.row.original;
          return <p className="text mb-0">{data?.country}</p>;
        },
      },
      {
        Header: "İlçe",
        accessor: "district",
        Cell: (response: any) => {
          const data = response.row.original;
          return <p className="text mb-0">{data?.district}</p>;
        },
      },
      {
        Header: "Mahalle",
        accessor: "neighbourhood",
        Cell: (response: any) => {
          const data = response.row.original;
          return <p className="text mb-0">{data?.neighbourhood}</p>;
        },
      },
      {
        Header: "Cadde/Sokak",
        accessor: "street",
        Cell: (response: any) => {
          const data = response.row.original;
          return <p className="text mb-0">{data?.street}</p>;
        },
      },
      {
        Header: "Kısa Tarif",
        accessor: "address_description",

        Cell: (response: any) => {
          return (
            <p className="text mb-0">
              {response.row.original?.address_description}
            </p>
          );
        },
      },
      {
        Header: "Telefon",
        accessor: "phone_number",
        Cell: (response: any) => {
          return (
            <p className="text mb-0">{response.row.original?.phone_number}</p>
          );
        },
      },
      {
        Header: "Sms",
        accessor: "is_visible",
        filterable: true,
        sortable: true,
        disableFilters: true,
        Cell: (response: any) => {
          const isActive = response.row.original.sms_returned_back;
          const color = isActive ? "success" : "danger";
          return (
            <div
              className={
                "badge bg-" + color + "-subtle text-" + color + " font-size-12"
              }
            >
              {isActive ? "Dönüş Yapıldı" : "Dönüş Yapılmadı"}
            </div>
          );
        },
      },
      {
        Header: "Kalan Gün",
        accessor: "remaining_date",
        filterable: false,
        disableFilters: true,
        Cell: (response: any) => (
          <>
            {
              <p className="text mb-0">
                {!response.row.original?.remaining_days.includes(" ")
                  ? 0
                  : response.row.original?.remaining_days.split(" ")[0]}
              </p>
            }
          </>
        ),
      },
    ],
    []
  );

  const mobileColumns = useMemo(
    () => [
      {
        Header: "Ad Soyad/Telefon",
        filterable: false,
        disableFilters: true,
        accessor: "fullname",
        Cell: (response: any) => (
          <>
            <h5 className="font-size-14 mb-1">
              <Link
                onClick={() =>
                  setCookieCustomerProfileId(response.row.original.id, 90)
                }
                to={
                  response.row.original.is_active
                    ? paths.CUSTOMER.CUSTORMER_PROFILE
                    : "#"
                }
                className="text-dark"
              >
                {response.row.original.fullname}

                <p className="font-size-13 text-muted mt-2">
                  {response.row.original.phone_number}
                </p>
              </Link>
            </h5>
          </>
        ),
      },
      {
        Header: "Kalan Gün",
        accessor: "remaining_date",
        filterable: false,
        disableFilters: true,
        Cell: (response: any) => (
          <>
            {
              <p className="text mb-0">
                {!response.row.original?.remaining_days.includes(" ")
                  ? 0
                  : response.row.original?.remaining_days.split(" ")[0]}
              </p>
            }
          </>
        ),
      },
    ],
    []
  );

  const filterByRemainingDays = (
    data: CustomerMaintenancesResponse | undefined,
    minDays: string,
    maxDays: string
  ) => {
    if (minDays === "" && maxDays === "") {
      return data?.results;
    }

    const convertMinDays = minDays ? parseInt(minDays) : -Infinity;
    const convertMaxDays = maxDays ? parseInt(maxDays) : Infinity;

    return data?.results.filter((item) => {
      let days = parseInt(item.remaining_days.split(" ")[0]);

      if (isNaN(days)) {
        return false;
      }

      return days >= convertMinDays && days <= convertMaxDays;
    });
  };

  return (
    <React.Fragment>
      <Row className="align-items-start">
        <Col md={6}>
          <div className="mb-3">
            <h5 className="card-title">
              Bakıma Kalan Gün Listesi
              <span className="text-muted fw-normal ms-2">
                {isLoading ? (
                  <Spinner className="ms-1" color="primary" />
                ) : (
                  `(${data?.results.length})`
                )}
              </span>
            </h5>
          </div>
        </Col>

        <Col md={6}>
          <div className="d-flex mb-3">
            <Form onSubmit={handleSubmit(onSubmit)} className="custom-form">
              <Row className="align-items-center justify-content-end">
                <Col xs="4" sm="4" md="4" lg="4">
                  <FormGroup>
                    <Label className="form-label">Başlangıç</Label>
                    <TextField
                      control={control}
                      name="startDay"
                      type="text"
                      className="form-control"
                      placeholder="Gün"
                      defaultValue={
                        filterState.minDays ? filterState.minDays : ""
                      }
                      error={errors.startDay?.message}
                    />
                  </FormGroup>
                </Col>

                <Col xs="4" sm="4" md="4" lg="4">
                  <FormGroup>
                    <Label htmlFor="validationCustom02">Bitiş</Label>
                    <TextField
                      control={control}
                      name="endDay"
                      className="form-control"
                      type="text"
                      placeholder="Gün"
                      defaultValue={
                        filterState.maxDays ? filterState.maxDays : ""
                      }
                      error={errors.endDay?.message}
                    />
                  </FormGroup>
                </Col>

                <Col xs="4" sm="4" md="4" lg="4">
                  <FormGroup>
                    <Button text="Filtrele" variant="primary" />
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg="12">
          {data?.results?.length ? (
            <DataTable
              columns={isMobile ? mobileColumns : columns}
              data={
                !!filteredMaintenancesList
                  ? filteredMaintenancesList
                  : data.results
              }
              isGlobalFilter={true}
              isAddUserList={true}
              setFilterState={setFilterState}
              pageIndexList={filterState.pageIndex}
              pageCountTable={
                filteredMaintenancesList?.count
                  ? filteredMaintenancesList?.count
                  : data.count
              }
              hasCustomPagination
              customPageSize={10}
              className="table align-middle datatable dt-responsive table-check nowrap"
            />
          ) : (
            !isLoading && <>Herhangi bir bakım bulunmamaktadır.</>
          )}
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default CustomerList;
